// Plans Service Module
// Fetches and caches plan data from the public API

import { CONFIG } from '../config.js';

let plansCache = null;
let plansFetchPromise = null;

/**
 * Fetch plans from the public API
 * Results are cached for the session
 * @returns {Promise<Array>} Array of plan objects
 */
export async function fetchPlans() {
    // Return cache if available
    if (plansCache) return plansCache;

    // Wait for existing fetch if one is in progress
    if (plansFetchPromise) return plansFetchPromise;

    plansFetchPromise = (async () => {
        try {
            const res = await fetch(`${CONFIG.API_BASE}/api/public/plans`);
            if (!res.ok) {
                console.error('Failed to fetch plans:', res.status);
                return [];
            }
            const responseData = await res.json();
            // API returns { data: { zone, currency, plans: [...] } }
            const plans = responseData.data?.plans || responseData.data || [];
            plansCache = Array.isArray(plans) ? plans : [];
            return plansCache;
        } catch (error) {
            console.error('Error fetching plans:', error);
            return [];
        } finally {
            plansFetchPromise = null;
        }
    })();

    return plansFetchPromise;
}

/**
 * Get the display name for a plan ID
 * @param {string} planId - The plan ID (free, medium, heavy)
 * @returns {string} The display name or the planId if not found
 */
export function getPlanDisplayName(planId) {
    if (!plansCache || !planId) return planId;
    const plan = plansCache.find(p => p.name === planId);
    return plan?.displayName || planId;
}

/**
 * Get full plan details by ID
 * @param {string} planId - The plan ID
 * @returns {object|null} The plan object or null if not found
 */
export function getPlanById(planId) {
    if (!plansCache || !planId) return null;
    return plansCache.find(p => p.name === planId) || null;
}

/**
 * Get all cached plans
 * @returns {Array|null} Array of plans or null if not loaded
 */
export function getAllPlans() {
    return plansCache;
}
